import React, { useState } from 'react';
import { TextField, Button, FormControl, Select, MenuItem, InputLabel, Alert, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { UserType } from '../../types/auth';
import { UseAuthFormReturn } from '../../types/form';

interface RegisterFormProps {
    formState: UseAuthFormReturn;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({ formState }) => {
    const { formData, error, loading, handleInputChange, handleSelectChange, handleRegister } = formState;

    const [showPassword, setShowPassword] = useState(false);

    const handleTogglePasswordVisibility = () => {
        setShowPassword((prevShowPassword) => !prevShowPassword);
    };
    const needsInviteCode = formData.userType === UserType.WORKER || formData.userType === UserType.ADMIN;

    return (
        <form onSubmit={handleRegister}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
                name="email"
                label="Email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
            />
            <TextField
                name="password"
                type={showPassword ? "text" : "password"}
                label="Password"
                value={formData.password}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleTogglePasswordVisibility}
                                edge="end"
                            >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
            />
            <TextField
                name="fullName"
                label="Full Name"
                value={formData.fullName}
                onChange={handleInputChange}
                fullWidth
                margin="normal"
            />
            <FormControl fullWidth margin="normal" sx={{ minHeight: '80px' }}>
                <InputLabel id="user-type-label">User Type</InputLabel>
                <Select
                    labelId="user-type-label"
                    name="userType"
                    value={formData.userType}
                    onChange={handleSelectChange}
                    label="User Type"
                >
                    <MenuItem value={UserType.REQUESTER}>Requester</MenuItem>
                    <MenuItem value={UserType.WORKER}>Worker</MenuItem>
                </Select>
            </FormControl>
            {needsInviteCode && (
                <TextField
                    name="inviteCode"
                    label="Invite Code"
                    value={formData.inviteCode || ''}
                    onChange={handleInputChange}
                    fullWidth
                    margin="normal"
                    required
                    helperText="Required for worker registration"
                />
            )}
            <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                fullWidth 
                disabled={loading || (needsInviteCode && !formData.inviteCode)}
            >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Register'}
            </Button>
        </form>
    );
};

export default RegisterForm;
